import { Link, useLocation } from 'react-router-dom'
import { Home, Store, ShoppingCart, User, ChefHat, Truck, Building2, Grid3X3, Plus, Package, ClipboardList, Users, Settings, Tag, DollarSign, Wallet, TrendingUp } from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'
import { useCart } from '../contexts/CartContext'
import { useStore } from '../contexts/StoreContext'
import ProfilePhoto from './ProfilePhoto'

const BottomNav = () => {
  const location = useLocation()
  const { user, isStoreOwner, isDispatcher, isChef, isDelivery, isPlatformAdmin, viewMode, isEmployee } = useAuth()
  const { itemCount } = useCart()
  const { stores, hasMultipleStores } = useStore()

  const getMainNavItems = () => {
    if (!user) {
      return [
        { path: '/', label: 'Inicio', icon: Home, shortLabel: 'Inicio' },
        { path: '/stores', label: 'Negocios', icon: Store, shortLabel: 'Tiendas' },
        { path: '/login', label: 'Perfil', icon: User, shortLabel: 'Login' },
      ]
    }

    // Si el usuario es empleado pero está en modo cliente, mostrar navegación de cliente
    if (isEmployee && viewMode === 'customer') {
      return [
        { path: '/', label: 'Inicio', icon: Home, shortLabel: 'Inicio' },
        { path: '/stores', label: 'Negocios', icon: Store, shortLabel: 'Tiendas' },
        { path: '/cart', label: 'Carrito', icon: ShoppingCart, shortLabel: 'Carrito', ...(itemCount > 0 && { badge: itemCount }) },
        { path: '/orders', label: 'Pedidos', icon: User, shortLabel: 'Pedidos' },
        { path: '/profile', label: 'Perfil', icon: User, shortLabel: 'Perfil' },
      ]
    }

    if (isStoreOwner) {
      const navItems = [
        { path: '/store-owner/my-stores', label: 'Mis Negocios', icon: Building2, shortLabel: 'Mis Tiendas' },
        { path: '/create-store', label: 'Crear', icon: Plus, shortLabel: 'Crear' },
        { path: '/stores', label: 'Negocios', icon: Store, shortLabel: 'Tiendas' },
        { path: '/profile', label: 'Perfil', icon: User, shortLabel: 'Perfil' },
      ]

      // Si tienen múltiples negocios, agregar dashboard de rendimiento
      if (hasMultipleStores) {
        navItems.splice(2, 0, { path: '/store/dashboard', label: 'Rendimiento', icon: TrendingUp, shortLabel: 'Estad.' })
      }

      // Si tienen negocios activos, cambiar el segundo botón por Dashboard
      if (stores.length > 0 && stores[0]) {
        navItems[1] = { path: '/store-owner/dashboard', label: 'Panel', icon: Building2, shortLabel: 'Panel' }
      }

      return navItems
    }

    if (isDispatcher) {
      const dispatchPath = '/dispatcher'
      return [
        { path: dispatchPath, label: 'Despacho', icon: Building2, shortLabel: 'Despacho' },
        { path: '/profile', label: 'Perfil', icon: User, shortLabel: 'Perfil' },
      ]
    }

    if (isChef) {
      return [
        { path: '/store/kitchen', label: 'Cocina', icon: ChefHat, shortLabel: 'Cocina' },
        { path: '/stores', label: 'Negocios', icon: Store, shortLabel: 'Tiendas' },
        { path: '/profile', label: 'Perfil', icon: User, shortLabel: 'Perfil' },
      ]
    }

    if (isDelivery) {
      const deliveryPath = user?.storeId ? `/store/${user.storeId}/delivery` : '/store/delivery'
      return [
        { path: deliveryPath, label: 'Entregas', icon: Truck, shortLabel: 'Entregas' },
        { path: '/store/delivery/wallet', label: 'Cartera', icon: Wallet, shortLabel: 'Cartera' },
        { path: '/profile', label: 'Perfil', icon: User, shortLabel: 'Perfil' },
      ]
    }

    if (isPlatformAdmin) {
      return [
        { path: '/admin/dashboard', label: 'Dashboard', icon: Building2, shortLabel: 'Panel' },
        { path: '/admin/orders', label: 'Pedidos', icon: Package, shortLabel: 'Pedidos' },
        { path: '/admin/users', label: 'Usuarios', icon: User, shortLabel: 'Usuarios' },
        { path: '/admin/categories', label: 'Categorías', icon: Tag, shortLabel: 'Cats' },
        { path: '/admin/stores', label: 'Aprobaciones', icon: Store, shortLabel: 'Aprob' },
      ]
    }

    // Cliente - Máximo 4 elementos principales (Inicio, Tiendas, Carrito, Pedidos) + Menú
    return [
      { path: '/', label: 'Inicio', icon: Home, shortLabel: 'Inicio' },
      { path: '/stores', label: 'Colmados', icon: Store, shortLabel: 'Tiendas' },
      { path: '/cart', label: 'Carrito', icon: ShoppingCart, shortLabel: 'Carrito', ...(itemCount > 0 && { badge: itemCount }) },
      { path: '/orders', label: 'Pedidos', icon: User, shortLabel: 'Pedidos' },
    ]
  }

  const getAllNavItems = () => {
    if (!user) {
      return getMainNavItems()
    }

    // Si el usuario es empleado pero está en modo cliente, mostrar opciones de cliente
    if (isEmployee && viewMode === 'customer') {
      return [
        { path: '/', label: 'Inicio', icon: Home },
        { path: '/stores', label: 'Colmados', icon: Store },
        { path: '/cart', label: 'Mi Carrito', icon: ShoppingCart },
        { path: '/orders', label: 'Mis Pedidos', icon: User },
        { path: '/profile', label: 'Mi Perfil', icon: User }
      ]
    }

    // Para usuarios logueados, mostrar todas las opciones disponibles
    const allItems = []

    if (isStoreOwner) {
      // Navegación básica siempre disponible
      allItems.push(
        { path: '/store-owner/my-stores', label: 'Mis Negocios', icon: Building2 },
        { path: '/create-store', label: 'Crear Nuevo Negocio', icon: Plus },
        { path: '/stores', label: 'Explorar Negocios', icon: Store },
        { path: '/profile', label: 'Mi Perfil', icon: User }
      )

      // Agregar navegación específica si tienen negocios
      if (stores.length > 0) {
        allItems.push(
          { path: '/store-owner/dashboard', label: 'Dashboard Principal', icon: Building2 }
        )

        // Agregar dashboard de rendimiento si tiene múltiples negocios
        if (hasMultipleStores) {
          allItems.push(
            { path: '/store/dashboard', label: 'Dashboard de Rendimiento', icon: TrendingUp }
          )
        }

        // Agregar todas las opciones de gestión del negocio para cada colmado
        stores.forEach(store => {
          allItems.push(
            { path: `/store/${store.id}/products`, label: `Inventario - ${store.name}`, icon: Package },
            { path: `/store/${store.id}/orders`, label: `Órdenes - ${store.name}`, icon: ClipboardList },
            { path: `/store/${store.id}/cash-register`, label: `Caja - ${store.name}`, icon: DollarSign },
            { path: `/store/${store.id}/delivery-wallets`, label: `Carteras Delivery - ${store.name}`, icon: Wallet },
            { path: `/store/${store.id}/employees`, label: `Empleados - ${store.name}`, icon: Users },
            { path: `/store/${store.id}/settings`, label: `Configuración - ${store.name}`, icon: Settings }
          )
        })

        // Categorías (compartidas entre colmados)
        allItems.push(
          { path: '/store/categories', label: 'Gestionar Categorías', icon: Tag }
        )
      }
    } else if (isDispatcher) {
      allItems.push(
        { path: '/dispatcher', label: 'Despacho', icon: Building2 },
        { path: `/store/${user?.storeId}/orders`, label: 'Órdenes', icon: ClipboardList },
        { path: `/store/${user?.storeId}/products`, label: 'Inventario', icon: Package },
        { path: `/store/${user?.storeId}/cash-register`, label: 'Caja', icon: DollarSign },
        { path: `/store/${user?.storeId}/delivery-wallets`, label: 'Carteras Delivery', icon: Wallet },
        { path: '/stores', label: 'Colmados', icon: Store },
        { path: '/profile', label: 'Mi Perfil', icon: User }
      )
    } else if (isChef) {
      allItems.push(
        { path: '/store/kitchen', label: 'Cocina', icon: ChefHat },
        { path: '/stores', label: 'Colmados', icon: Store },
        { path: '/profile', label: 'Mi Perfil', icon: User }
      )
    } else if (isDelivery) {
      const deliveryPath = user?.storeId ? `/store/${user.storeId}/delivery` : '/store/delivery'
      allItems.push(
        { path: deliveryPath, label: 'Entregas', icon: Truck },
        { path: '/store/delivery/wallet', label: 'Mi Cartera', icon: Wallet },
        { path: '/profile', label: 'Mi Perfil', icon: User }
      )
    } else if (isPlatformAdmin) {
      allItems.push(
        { path: '/admin/dashboard', label: 'Dashboard Admin', icon: Building2 },
        { path: '/admin/orders', label: 'Gestión de Pedidos', icon: Package },
        { path: '/admin/users', label: 'Gestión de Usuarios', icon: User },
        { path: '/admin/categories', label: 'Categorías Globales', icon: Tag },
        { path: '/admin/store-owners', label: 'Dueños de Negocio', icon: Store },
        { path: '/admin/stores', label: 'Aprobación de Negocios', icon: Store },
        { path: '/admin/store-owner-requests', label: 'Solicitudes Dueños', icon: User },
        { path: '/profile', label: 'Mi Perfil', icon: User }
      )
    } else {
      // Customer
      allItems.push(
        { path: '/', label: 'Inicio', icon: Home },
        { path: '/stores', label: 'Colmados', icon: Store },
        { path: '/cart', label: 'Mi Carrito', icon: ShoppingCart },
        { path: '/orders', label: 'Mis Pedidos', icon: User },
        { path: '/profile', label: 'Mi Perfil', icon: User }
      )
    }

    return allItems
  }

  const mainNavItems = getMainNavItems()
  const allNavItems = getAllNavItems()
  
  // Separar el perfil de los demás items
  const profileItem = mainNavItems.find(item => item.path === '/profile')
  const itemsWithoutProfile = mainNavItems.filter(item => item.path !== '/profile')
  
  // Determinar si necesitamos el botón de menú (si hay más de 4 items en total)
  // Mostraremos máximo 3 items principales + perfil + menú = 5 items total
  const needsMenuButton = allNavItems.length > 4 && user
  
  // Construir los items a mostrar
  let displayItems = []
  
  if (needsMenuButton) {
    // Si necesitamos el menú, mostrar 3 items principales + perfil + menú
    displayItems = [...itemsWithoutProfile.slice(0, 3)]
    if (profileItem) {
      displayItems.push(profileItem)
    } else if (user) {
      // Si no hay perfil en mainNavItems pero el usuario está logueado, agregarlo
      displayItems.push({
        path: '/profile',
        label: 'Perfil',
        shortLabel: 'Perfil',
        icon: User
      })
    }
    displayItems.push({
      path: '/menu',
      label: 'Más',
      shortLabel: 'Más',
      icon: Grid3X3
    })
  } else {
    // Si no necesitamos el menú, mostrar todos los items (incluyendo perfil si existe)
    displayItems = [...mainNavItems]
    // Si no hay perfil en mainNavItems pero el usuario está logueado, agregarlo al final
    if (user && !profileItem && displayItems.length < 5) {
      displayItems.push({
        path: '/profile',
        label: 'Perfil',
        shortLabel: 'Perfil',
        icon: User
      })
    }
  }

  return (
    <div className="lg:hidden fixed bottom-0 left-0 right-0 z-[99999] bg-white/90 backdrop-blur-2xl border-t border-gray-200/60 shadow-2xl">
      <div className="flex justify-around items-center py-2 px-2 safe-area-pb">
        {displayItems.map((item) => {
          const Icon = item.icon
          const isActive = location.pathname === item.path ||
            (item.path === '/' && location.pathname === '/home') ||
            (item.path === '/store-owner/dashboard' && location.pathname.includes('/store-owner')) ||
            (item.path === '/menu' && location.pathname === '/menu')

          return (
            <Link
              key={item.path}
              to={item.path}
              className={`flex flex-col items-center justify-center py-2 px-2 sm:px-3 rounded-2xl transition-all duration-200 min-w-0 flex-1 relative group ${
                isActive
                  ? 'text-primary-600'
                  : 'text-gray-600 hover:text-primary-600 active:scale-95'
              }`}
            >
              <div className="relative mb-1">
                {item.path === '/profile' && user ? (
                  <ProfilePhoto 
                    photoUrl={user.profilePhotoUrl || (user as any).profile_photo_url} 
                    size="sm" 
                    showBorder={false}
                    className={`transition-transform duration-200 ${isActive ? 'scale-110' : 'group-hover:scale-105'}`}
                  />
                ) : (
                  <div className={`relative ${isActive ? 'bg-primary-50 p-2 rounded-xl' : 'group-hover:bg-gray-50 p-2 rounded-xl transition-colors duration-200'}`}>
                    <Icon className={`w-5 h-5 transition-all duration-200 ${
                      isActive ? 'scale-110' : 'group-hover:scale-105'
                  }`} />
                  </div>
                )}
                {'badge' in item && item.badge && Number(item.badge) > 0 && (
                  <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] rounded-full min-w-[20px] h-5 flex items-center justify-center font-bold shadow-md px-1.5 border-2 border-white">
                    {Number(item.badge) > 99 ? '99+' : item.badge}
                  </span>
                )}
              </div>
              <span className={`text-[10px] sm:text-[11px] font-semibold transition-all duration-200 leading-tight text-center ${
                isActive ? 'scale-105' : ''
              }`}>
                {item.shortLabel || item.label}
              </span>
              {isActive && (
                <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-8 h-1 bg-primary-600 rounded-full"></div>
              )}
            </Link>
          )
        })}
      </div>
      {/* Safe area spacing for iPhone */}
      <div className="h-safe-area-inset-bottom bg-transparent"></div>
    </div>
  )
}

export default BottomNav
